import type { ReactNode } from 'react';
import type { SearchResult } from '../../types';
import { type SearchTypes } from '../../types';

import styles from './start-page-list.module.css';
import { Button, StickyHeader } from '@bi-digital/react';
import { Result } from '../result/Result';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, ChevronRightIcon, GroupUnitIcon, JournalIcon, SchoolIcon } from '@bi-digital/icons';
import { Link, useSearchParams } from 'react-router';

const TypeToIconAndTitle: Record<Exclude<SearchTypes, 'All'>, { icon: ReactNode; title: string }> = {
    Articles: {
        icon: <JournalIcon aria-hidden='true' size='medium' />,
        title: 'tabs.articles',
    },
    Employees: {
        icon: <GroupUnitIcon aria-hidden='true' size='medium' />,
        title: 'tabs.employees',
    },
    Events: {
        icon: <CalendarIcon aria-hidden='true' size='medium' />,
        title: 'tabs.events',
    },
    Programmes: {
        icon: <SchoolIcon aria-hidden='true' size='medium' />,
        title: 'tabs.programmes',
    },
};

export type StartPageListProps = {
    type: Exclude<SearchTypes, 'All'>;
    count: number;
    results: SearchResult[];
};

export const StartPageList = ({ type, count, results }: StartPageListProps) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    if (results.length === 0) {
        return null;
    }

    return (
        <div className={styles.list}>
            <StickyHeader>
                <div className={styles.header}>
                    {TypeToIconAndTitle[type].icon}
                    <h2>{t(TypeToIconAndTitle[type].title)}</h2>
                </div>
            </StickyHeader>
            <div className={styles.results}>
                {results.map((result) => (
                    <Result key={result.id} result={result} />
                ))}
            </div>
            {count > results.length && (
                <Button variant='text' endIcon={<ChevronRightIcon aria-hidden='true' />} asChild>
                    <Link
                        to={`?type=${type}&q=${searchParams.get('q') || ''}`}
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                        {t('showAll', { count })}
                    </Link>
                </Button>
            )}
        </div>
    );
};
